"use client";

import { useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/use-auth';

const VAPID_PUBLIC_KEY = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY;

export function PushManager() {
    const { user } = useAuth();

    const urlBase64ToUint8Array = (base64String: string) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
            .replace(/\-/g, '+')
            .replace(/_/g, '/');

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    };

    const subscribeUser = useCallback(async (registration: ServiceWorkerRegistration) => {
        if (!VAPID_PUBLIC_KEY) return;

        try {
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
            });

            console.log('[PUSH] Subscribed:', subscription);

            await fetch('/api/push', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    subscription,
                    action: 'subscribe'
                })
            });
        } catch (error) {
            console.error('[PUSH] Subscription error:', error);
        }
    }, []);

    useEffect(() => {
        if (!user || typeof window === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window)) {
            return;
        }

        const registerSWAndSubscribe = async () => {
            try {
                const registration = await navigator.serviceWorker.register('/sw.js', {
                    scope: '/'
                });
                console.log('[PUSH] Service Worker registered');

                // Check for existing subscription
                const existingSubscription = await registration.pushManager.getSubscription();

                if (Notification.permission === 'granted') {
                    if (!existingSubscription) {
                        await subscribeUser(registration);
                    } else {
                        // Refresh subscription on backend just in case
                        await fetch('/api/push', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                subscription: existingSubscription,
                                action: 'subscribe'
                            })
                        });
                    }
                }
                return registration;
            } catch (error) {
                console.error('[PUSH] SW Registration error:', error);
            }
        };

        const handleManualSubscribe = async () => {
            const registration = await registerSWAndSubscribe();
            if (registration && Notification.permission === 'granted') {
                await subscribeUser(registration);
            }
        };

        window.addEventListener('subscribe-push', handleManualSubscribe);
        registerSWAndSubscribe();

        return () => {
            window.removeEventListener('subscribe-push', handleManualSubscribe);
        };
    }, [user, subscribeUser]);

    return null;
}
